import { useState } from 'react';
import type {
  PickedProductLength,
  ProductLengthPick,
} from '~/components/common/productLengthChoice';
import type { useGetSellerPriceForBuyer } from '~/api/inventory';
import { UNIT_PRICE_MAX_DECIMALS } from '~/utils/unitPrice';
import { getPlatformProductDimensions } from '~/utils/order';
import { toStoragePriceOrNull } from '~/utils/unitConversion';

type SellerPrice = ReturnType<typeof useGetSellerPriceForBuyer>['price'];

/**
 * What to send as the suggested price when adding:
 * - `send`: the buyer's price, in the product's storage unit
 * - `clear`: drop the suggested price on the line being replaced
 * - `server`: send nothing, the server prices the line
 * - `unconvertible`: the buyer's price cannot be converted to the storage unit
 */
export type PriceForAdd =
  | { kind: 'send'; price: number }
  | { kind: 'clear' }
  | { kind: 'server' }
  | { kind: 'unconvertible' };

function sellerPriceAmount(
  picked: ProductLengthPick,
  sellerPrice: SellerPrice
): number | null {
  if (
    !sellerPrice ||
    picked.kind !== 'length' ||
    !picked.platformProductsLength ||
    !picked.quantity
  )
    return null;
  const { platformProduct, length, isNominal } = picked.platformProductsLength;
  return toStoragePriceOrNull(
    sellerPrice.amount,
    sellerPrice.unit,
    picked.quantity.unit,
    getPlatformProductDimensions(platformProduct, length, isNominal)
  );
}

function priceInStorageUnit(picked: PickedProductLength, amount: number) {
  const { platformProduct, length, isNominal } = picked.platformProductsLength;
  return toStoragePriceOrNull(
    amount,
    picked.quantity.unit,
    picked.standardUnitsCode,
    getPlatformProductDimensions(platformProduct, length, isNominal)
  );
}

/**
 * Price shown and edited in the catalog add row. The seller's price is shown
 * converted to the picked unit; a buyer edit overrides it until the pick changes.
 */
export function useCatalogAddPrice(
  picked: ProductLengthPick,
  pickedLengthId: string | null | undefined,
  sellerPrice: SellerPrice,
  replacesCartLine: boolean
) {
  const prefill = sellerPriceAmount(picked, sellerPrice);
  const pickedUnit =
    picked.kind === 'length' && picked.quantity
      ? picked.quantity.unit
      : undefined;
  const priceKey = `${pickedLengthId}:${pickedUnit}`;
  const [editedPrice, setEditedPrice] = useState<{
    key: string;
    value: number;
  } | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const isEditing = editingKey === priceKey;
  const isEdited = editedPrice?.key === priceKey;
  const typedPrice = isEdited ? editedPrice.value : prefill;
  const showSellerUnit = !isEdited && prefill == null && !!sellerPrice;
  const round = (price: number | null) =>
    price == null ? null : Number(price.toFixed(UNIT_PRICE_MAX_DECIMALS));
  const defaultValue = round(typedPrice);

  const priceForAdd = (complete: PickedProductLength): PriceForAdd => {
    if (isEdited) {
      const price = priceInStorageUnit(complete, editedPrice.value);
      return price == null
        ? { kind: 'unconvertible' }
        : { kind: 'send', price };
    }
    return replacesCartLine ? { kind: 'clear' } : { kind: 'server' };
  };

  return {
    display: {
      price: showSellerUnit ? sellerPrice.amount : typedPrice,
      unit: showSellerUnit ? sellerPrice.unit : (pickedUnit ?? ''),
    },
    priceForAdd,
    edit: {
      isEdited,
      isEditing,
      defaultValue,
      start: () => setEditingKey(priceKey),
      cancel: () => setEditingKey(null),
      submit: (value: number) => {
        setEditedPrice(
          value === round(prefill) ? null : { key: priceKey, value }
        );
        setEditingKey(null);
      },
      reset: () => {
        setEditedPrice(null);
        setEditingKey(null);
      },
    },
  };
}
